import {
  DonationUnitType,
  DonorStatus,
  useGetPrbcDonationDonorsQuery,
} from 'gql/types';
import { useMemo } from 'react';

import { getDateRangeBounds } from '../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { IDonorRecord } from '../types/donor';
import { DateRangeType } from '../types/phlebotomist-dashboard';

export interface IUsePrbcDonationDonorsProps {
  dateRange?: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionIds?: string[];
  officeIds?: string[];
  locationIds?: string[];
  activeDonorStatuses?: DonorStatus[];
  /** Filter donors by registration date (createdAt). ISO date strings. */
  registrationRange?: { start: string; end: string };
  /** Filter to lapsed donors whose last donation was more than N days ago. */
  lapsedDays?: number;
  /** Filter to retired (Unresponsive) donors — lower bound of days-ago range (inclusive) */
  retiredDaysFrom?: number;
  /** Filter to retired (Unresponsive) donors — upper bound of days-ago range (exclusive). Null = no upper bound. */
  retiredDaysTo?: number;
  /** Filter to donors retiring due to age within N days (30/60/90). */
  retiringInDays?: number;
  /** Restrict to donors with an Active transition in status history or a donation. */
  requireActiveHistory?: boolean;
  /** Restrict to donors with a Retired transition recorded in status history. */
  requireRetiredHistory?: boolean;
  /** Restrict to donors with an Approved transition recorded in status history. */
  requireApprovedHistory?: boolean;
  /** Restrict to donors with an Archived transition recorded in status history. */
  requireArchivedHistory?: boolean;
  donationUnitType?: DonationUnitType;
  sglThresholdMl?: number;
  enabled?: boolean;
}

export function usePrbcDonationDonors(props: IUsePrbcDonationDonorsProps) {
  const {
    dateRange,
    customDateRange,
    regionIds,
    officeIds,
    locationIds,
    activeDonorStatuses,
    registrationRange,
    lapsedDays,
    retiredDaysFrom,
    retiredDaysTo,
    retiringInDays,
    requireActiveHistory,
    requireRetiredHistory,
    requireApprovedHistory,
    requireArchivedHistory,
    donationUnitType,
    sglThresholdMl,
    enabled = true,
  } = props;

  const periodRange = useMemo(
    () => (dateRange ? getDateRangeBounds(dateRange, customDateRange) : null),
    [dateRange, customDateRange]
  );

  const { data, loading, error } = useGetPrbcDonationDonorsQuery({
    skip: !enabled,
    variables: {
      input: {
        periodRange: periodRange
          ? { start: periodRange.start, end: periodRange.end }
          : undefined,
        regionIds: regionIds?.length ? regionIds : undefined,
        officeIds: officeIds?.length ? officeIds : undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
        activeDonorStatuses: activeDonorStatuses?.length
          ? activeDonorStatuses
          : undefined,
        registrationRange: registrationRange || undefined,
        lapsedDays: lapsedDays || undefined,
        retiredDaysFrom: retiredDaysFrom ?? undefined,
        retiredDaysTo: retiredDaysTo ?? undefined,
        retiringInDays: retiringInDays ?? undefined,
        requireActiveHistory: requireActiveHistory ?? undefined,
        requireRetiredHistory: requireRetiredHistory ?? undefined,
        requireApprovedHistory: requireApprovedHistory ?? undefined,
        requireArchivedHistory: requireArchivedHistory ?? undefined,
        donationUnitType: donationUnitType ?? undefined,
        sglThresholdMl: sglThresholdMl ?? undefined,
      },
    },
  });

  const donors: IDonorRecord[] = useMemo(() => {
    return (data?.getPrbcDonationDonors?.donors ?? []).map((d) => ({
      donorId: d.donorId,
      donorShortId: d.donorShortId,
      donorName: d.donorName,
      ownerFullName: d.ownerFullName ?? null,
      ownerShortId: d.ownerShortId ?? null,
      bloodTypeDisplay: d.bloodTypeDisplay,
      status: d.status,
      lastDonationDate: d.lastDonationDate ?? null,
      sglDbl: d.sglDbl,
      totalDonations: d.totalDonations,
      periodUnits: d.periodUnits,
      location: d.location,
      registeredAt: d.registeredAt ?? null,
      ownerPhone: d.ownerPhone ?? null,
      ownerEmail: d.ownerEmail ?? null,
      ownerCity: d.ownerCity ?? null,
      ownerState: d.ownerState ?? null,
      ownerContactPreferences: d.ownerContactPreferences ?? null,
      ownerAssignedLocations: d.ownerAssignedLocations ?? null,
      breed: d.breed ?? null,
      dateOfBirth: d.dateOfBirth ?? null,
      region: d.region ?? null,
      retiredDate: d.retiredDate ?? null,
      retiredReason: d.retiredReason ?? null,
      activeDate: d.activeDate ?? null,
      approvedDate: d.approvedDate ?? null,
      archivedDate: d.archivedDate ?? null,
    }));
  }, [data]);

  return {
    donors,
    total: data?.getPrbcDonationDonors?.total ?? 0,
    loading,
    error,
    periodRange,
  };
}
